/**
 * Что можно достать из ИИН: дату рождения и пол.
 *
 * Первые шесть цифр — дата рождения, седьмая кодирует век вместе с полом:
 * 1 и 2 — девятнадцатый век, 3 и 4 — двадцатый, 5 и 6 — двадцать первый;
 * нечётная — мужчина, чётная — женщина.
 *
 * Нужно, чтобы не заставлять диспетчера вводить руками то, что и так есть в номере. Но
 * подставленное значение остаётся правимым: ИИН выдают и негражданам, встречаются исключения,
 * и запрет «как в ИИН, и точка» упёрся бы в первый же нестандартный случай.
 *
 * Обратная сторона той же монеты: если введённая дата с номером не сходится, значит где-то
 * опечатка. Цифры ИИН никто глазами не читает, и это единственный способ её заметить.
 */

export const SEX_VALUES = ['MALE', 'FEMALE', 'UNKNOWN'] as const;
export type Sex = (typeof SEX_VALUES)[number];

export const SEX_LABELS: Record<Sex, string> = {
  MALE: 'Мужской',
  FEMALE: 'Женский',
  UNKNOWN: 'Не указан',
};

export interface IinFacts {
  /** Дата рождения в виде `YYYY-MM-DD`. */
  birthDate: string;
  sex: Sex;
}

const IIN_LENGTH = 12;
const CENTURY_BY_DIGIT: Record<string, number> = {
  '1': 1800,
  '2': 1800,
  '3': 1900,
  '4': 1900,
  '5': 2000,
  '6': 2000,
};

/** `null` — номер не разобрался: не та длина, не цифры, неизвестный век или несуществующая дата. */
export function parseIin(iin: string | null | undefined): IinFacts | null {
  const digits = (iin ?? '').trim();
  if (digits.length !== IIN_LENGTH || !/^\d+$/.test(digits)) {
    return null;
  }

  const century = CENTURY_BY_DIGIT[digits[6]];
  if (century === undefined) {
    return null;
  }

  const year = century + Number(digits.slice(0, 2));
  const month = Number(digits.slice(2, 4));
  const day = Number(digits.slice(4, 6));

  const date = new Date(Date.UTC(year, month - 1, day));
  const real =
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day;
  if (!real) {
    return null;
  }

  return {
    birthDate: date.toISOString().slice(0, 10),
    sex: Number(digits[6]) % 2 === 1 ? 'MALE' : 'FEMALE',
  };
}

/** Расходится ли заведённое вручную с тем, что зашито в номере. Пустое поле расхождением не считаем. */
export function iinMismatch(
  iin: string | null | undefined,
  birthDate: string | null | undefined,
  sex: Sex | null | undefined,
): string | null {
  const facts = parseIin(iin);
  if (!facts) {
    return null;
  }
  const problems = [
    birthDate && birthDate !== facts.birthDate ? `дата рождения (в ИИН ${facts.birthDate})` : null,
    sex && sex !== 'UNKNOWN' && sex !== facts.sex
      ? `пол (в ИИН ${SEX_LABELS[facts.sex].toLowerCase()})`
      : null,
  ].filter((item): item is string => item !== null);

  return problems.length === 0 ? null : `Не сходится с ИИН: ${problems.join(', ')}`;
}
